import { Injectable } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { Resolve } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ENDPOINTS } from 'src/app/_configs/endpoints';
import { ApiService } from './../../modules/shared/_services/api.service';

// Define state keys for each API endpoint
const STATES_LIST = makeStateKey<any>('statelisting');
const CATEGORIES_LIST = makeStateKey<any>('categorylisting');
const CITIES_LIST = makeStateKey<any>('citylisting');
const KEYWORDS_LIST = makeStateKey<any>('keywordlisting');
const AUTHORITIES_LIST = makeStateKey<any>('authoritylisting');
const PROCUREMENT_LIST = makeStateKey<any>('procurementlisting');
const TENDER_TOP_TEN_CITIES = makeStateKey<any>('topcitiesing');

@Injectable({
  providedIn: 'root'
})
export class TendersListingResolver implements Resolve<any> {

  constructor(
    private apiService: ApiService,
    private transferState: TransferState
  ) { }

  resolve(): Observable<any> {
    // Check if data is available in TransferState
    if (
      this.transferState.hasKey(STATES_LIST) &&
      this.transferState.hasKey(CATEGORIES_LIST) &&
      this.transferState.hasKey(CITIES_LIST) &&
      this.transferState.hasKey(KEYWORDS_LIST) &&
      this.transferState.hasKey(AUTHORITIES_LIST) &&
      this.transferState.hasKey(PROCUREMENT_LIST) &&
      this.transferState.hasKey(TENDER_TOP_TEN_CITIES)
    ) {
      // Retrieve and remove cached data from TransferState
      const data = {
        states: this.transferState.get(STATES_LIST, {}),
        categories: this.transferState.get(CATEGORIES_LIST, {}),
        cities: this.transferState.get(CITIES_LIST, {}),
        keywords: this.transferState.get(KEYWORDS_LIST, {}),
        authorities: this.transferState.get(AUTHORITIES_LIST, {}),
        procurement: this.transferState.get(PROCUREMENT_LIST, {}),
        topCities: this.transferState.get(TENDER_TOP_TEN_CITIES, {})
      };

      // Remove the cached data
      this.transferState.remove(STATES_LIST);
      this.transferState.remove(CATEGORIES_LIST);
      this.transferState.remove(CITIES_LIST);
      this.transferState.remove(KEYWORDS_LIST);
      this.transferState.remove(AUTHORITIES_LIST);
      this.transferState.remove(PROCUREMENT_LIST);
      this.transferState.remove(TENDER_TOP_TEN_CITIES);
      return of(data);
    } else {
      // Define API URLs
      const urls = {
        statesUrl: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_STATE_LIST}`,
        categoriesUrl: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_CATEGORY_LIST}`,
        citylistUrl: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_CITY_LIST}`,
        keywordsUrl: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_KEYWORDS_LIST}`,
        authoritiesUrl: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_AUTHORITY_LIST}`,
        procurementUrl: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_PROCUREMENT_LIST}`,
        topcitiesUrl: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.RETRIEVE_TENDER_BY_CITIES}`
      };

      // Fetch data from APIs concurrently with error handling
      return forkJoin({
        states: this.apiService.get(urls.statesUrl).pipe(
          timeout(5000),
          catchError(error => {
            console.error('Error fetching states:', error);
            return of({ data: [] });
          })
        ),
        categories: this.apiService.get(urls.categoriesUrl).pipe(
          timeout(5000),
          catchError(error => {
            console.error('Error fetching categories:', error);
            return of({ data: [] });
          })
        ),
        cities: this.apiService.get(urls.citylistUrl).pipe(
          timeout(5000),
          catchError(error => {
            console.error('Error fetching cities:', error);
            return of({ data: [] });
          })
        ),
        keywords: this.apiService.get(urls.keywordsUrl).pipe(
          timeout(5000),
          catchError(error => {
            console.error('Error fetching keywords:', error);
            return of({ data: [] });
          })
        ),
        authorities: this.apiService.get(urls.authoritiesUrl).pipe(
          timeout(5000),
          catchError(error => {
            console.error('Error fetching authorities:', error);
            return of({ data: [] });
          })
        ),
        procurement: this.apiService.get(urls.procurementUrl).pipe(
          timeout(5000),
          catchError(error => {
            console.error('Error fetching procurement:', error);
            return of({ data: [] });
          })
        ),
        topCities: this.apiService.get(urls.topcitiesUrl).pipe(
          timeout(5000),
          catchError(error => {
            console.error('Error fetching top cities:', error);
            return of({ data: [] });
          })
        )
      }).pipe(
        tap(data => {
          // Cache data to Transfer State for SSR
          this.transferState.set(STATES_LIST, data.states);
          this.transferState.set(CATEGORIES_LIST, data.categories);
          this.transferState.set(CITIES_LIST, data.cities);
          this.transferState.set(KEYWORDS_LIST, data.keywords);
          this.transferState.set(AUTHORITIES_LIST, data.authorities);
          this.transferState.set(PROCUREMENT_LIST, data.procurement);
          this.transferState.set(TENDER_TOP_TEN_CITIES, data.topCities);
        }),
        catchError(error => {
          console.error('Error during data resolution:', error);
          // Return whatever data is successfully loaded, with empty structures for failed calls
          return of({
            states: { data: [] },
            categories: { data: [] },
            cities: { data: [] },
            keywords: { data: [] },
            authorities: { data: [] },
            procurement: { data: [] },
            topCities: { data: [] }
          });
        })
      );
    }
  }
}